import { useEffect, useState, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Box,
    DialogContentText,
    Typography,
    CircularProgress,
    Tabs, Tab, 
} from '@mui/material';
import {AppRegistration, Category, Person} from '@mui/icons-material';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import { listaEmpresas } from '../../management/companies/configs/functions';
import {
    listaGruposCategoria, listaGruposCategoriaEProdutos,
} from '../products/configs/functions';
import { listaPermissoes, manipulaGHEs } from './config/functions';
import { getFormErrorMessage } from '../../../../configs/functions';
import { useModuleContext } from '../../../../contexts/ModuleProvider';
import {InspectionIcon} from "../../../common/Icons";
import {GHEFormFirstStep} from "./components/GHEFormFirstStep";
import {GHEFormSecondStep} from "./components/GHEFormSecondStep";
import {listaUsuariosFilter} from "../../management/people/configs/functions";
import {GHEFormThirdStep} from "./components/GHEFormThirdStep";
import {GHECategoryDetails} from "./components/GHECategoryDetails";
import {useProcessamentos} from "../../../../contexts/ProcessamentosProvider";
import {isGrupoTravado, mensagemGrupoTravado} from "./config/processamentos";

function GHEForm({
                     open,
                     openCategoryDetailDialog,
                     setOpenCategoryDetailDialog,
                     openDeleteDialog,
                     actionsAllowed,
                     setOpen,
                     selectedGHE,
                     queryKey
                }) {
    const queryClient = useQueryClient();
    const { exibirAlerta, exibirDialog, handleCloseDialog } = useCommonItems();
    const { getEmpresasFilteredByModule } = useModuleContext();
    const { acompanhar } = useProcessamentos();

    const [gheEmpresaId, setGheEmpresaId] = useState(null);

    const isEditAction = !!selectedGHE;
    const allowInteraction = (!isEditAction || (isEditAction && actionsAllowed));
    const desativado = isEditAction && !selectedGHE?.status

    const {
        register,
        unregister,
        handleSubmit,
        getValues,
        setValue,
        watch,
        control,
        formState: { errors, isSubmitting },
        reset,
    } = useForm();

    const enviandoRef = useRef(false);

    const [cards, setCards] = useState([]);
    const [existingCards, setExistingCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState(null);
    const [selectedCardInfo, setSelectedCardInfo] = useState([]);

    const [categoriasEProdutos, setCategoriasEProdutos] = useState([]);
    const [permissoes, setPermissoes] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [produtos, setProdutos] = useState([]);
    const [pessoas, setPessoas] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [alreadyLoadInitialCards, setAlreadyLoadInitialCards] = useState(false);

    const [selectedTab, setSelectedTab] = useState(null);

    const carregarEmpresas = async () => {
        try {
            exibirDialog(<Typography><CircularProgress size={20} />Buscando empresas...</Typography>);
            const res = await listaEmpresas(null, null, null, true);
            const filteredEmpresas = getEmpresasFilteredByModule('Setor', res.data.data, !isEditAction);
            setEmpresas(filteredEmpresas);
        } catch (error) {
            console.log('Erro ao carregar empresas:', error);
        } finally {
            handleCloseDialog();
        }
    };

    const carregarPessoas = async () => {
        try {
            exibirDialog(<Typography><CircularProgress size={20} />Buscando colaboradores...</Typography>);
            const res = await listaUsuariosFilter({ id_grupo: selectedGHE?.id, isFilter: true });
            setPessoas(res.data.data);
        } catch (error) {
            console.log('Erro ao carregar colaboradores:', error);
        } finally {
            handleCloseDialog();
        }
    }

    const carregaCategorias = async () => {
        setIsLoading(true);
        exibirDialog(
            <Typography>
                <CircularProgress size={20} /> Buscando categorias e produtos para a empresa selecionada...
            </Typography>
        );
        const idEmpresa = getValues('id_empresa');

        try {
            const res = await listaGruposCategoriaEProdutos({ id_empresa: idEmpresa });
            const categorias = res.data;

            const resGrupos = await listaGruposCategoria({ id_grupo: selectedGHE?.id ?? '' });
            const gruposCategorias = resGrupos.data.data;

            categorias.forEach((categoria) => {
                const grupo = gruposCategorias.find((gc) => gc.categoria?.id === categoria.id);
                if (grupo) {
                    categoria.quantidade_maxima = grupo.quantidadeMaxima;
                    categoria.periodo = grupo.periodo;
                    categoria.ponto_alerta = grupo.pontoAlerta;
                }

                categoria.disabled = categoria.sem_produtos === true;
                categoria.label = categoria.nome + (categoria.sem_produtos ? ' - (Sem produtos vinculados)' : '');
            });

            setCategoriasEProdutos(categorias);
        } catch (error) {
            console.error('Erro ao carregar categorias: ' + error);
        } finally {
            handleCloseDialog();
            setIsLoading(false);
        }
    };

    async function loadCompanyData(GHEId) {
        await carregaCategorias();
        await carregaPermissoesByGrupoId(GHEId);

        setIsLoading(false);
    }

    async function carregaPermissoesByGrupoId(id_grupo) {
        try {
            const response = await listaPermissoes({ id_grupo });
            // response.data.data.forEach((permissao) => {
            //     permissao.is_dirty = false;
            // });
            setPermissoes(response.data.data);
        } catch (error) {
            exibirAlerta('Erro ao carregar as Permissões', '', 'error');
        }
    }

    function createCardsInEdit() {
        if (alreadyLoadInitialCards) {
            return;
        }

        try {
            // buscar categorias selecionadas, a partir das permissões
            const selectedCategories = categoriasEProdutos.filter(categoria => {
                return permissoes.find(permissao => permissao.categoria.id === categoria.id);
            });

            setCards(selectedCategories);
            setExistingCards(selectedCategories);

            // apos listar categorias selecionadas, setar os default values para os campos
            // e setar os valores dos produtos
            // setar os valores dos produtos
            selectedCategories.forEach(categoria => {
                register(`categorias[${categoria.id}].id_categoria`, { value: categoria.id });
                setValue(`categorias[${categoria.id}].quantidade_maxima`, categoria.quantidade_maxima);
                setValue(`categorias[${categoria.id}].periodo`, categoria.periodo);
                setValue(`categorias[${categoria.id}].ponto_alerta`, categoria.ponto_alerta);

                //registra os campos dos produtos baseado em permissões produto.id
                categoria.produtos.forEach(produto => {
                    const permissao = permissoes.find(permissao => permissao.produto.id === produto.id);

                    const overwriteCategoryValues = !permissao?.infoCategoria;

                    if (!permissao) {
                        return;
                    }

                    register(`permissoes[${produto.id}].id_permissao`, { value: permissao.id });

                    register(`permissoes[${produto.id}].id_produto`, { value: produto.id });
                    register(`permissoes[${produto.id}].id_categoria`, { value: produto.idCategoria ?? produto.id_categoria });
                    register(`permissoes[${produto.id}].sobrescreverCategoria`, { value: overwriteCategoryValues });
                    register(`permissoes[${produto.id}].sobrescreverCategoriaAntigo`, { value: overwriteCategoryValues });
                    register(`permissoes[${produto.id}].status`, { value: permissao.status });
                    register(`permissoes[${produto.id}].statusAntigo`, { value: permissao.status });

                    if (permissao && overwriteCategoryValues) {
                        register(`permissoes[${produto.id}].periodo`, { value: permissao.periodo });
                        register(`permissoes[${produto.id}].periodoAntigo`, { value: permissao.periodo });
                        register(`permissoes[${produto.id}].ponto_alerta`, { value: permissao.pontoAlerta });
                        register(`permissoes[${produto.id}].ponto_alertaAntigo`, { value: permissao.pontoAlerta });
                    } else {
                        register(`permissoes[${produto.id}].periodo`, { value: categoria.periodo });
                        register(`permissoes[${produto.id}].periodoAntigo`, { value: categoria.periodo });
                        register(`permissoes[${produto.id}].ponto_alerta`, { value: categoria.ponto_alerta });
                        register(`permissoes[${produto.id}].ponto_alertaAntigo`, { value: categoria.ponto_alerta });
                    }

                    register(`permissoes[${produto.id}].quantidade_maxima`, { value: categoria.quantidade_maxima });
                });
            });
        } catch (error) {
            console.log('Erro ao carregar categorias e produtos:', error);
        } finally {
            setAlreadyLoadInitialCards(true);
        }
    }

    const onSubmit = async (data) => {
        if (data.categorias) {
            data.categorias = data.categorias.filter(categoria => categoria != null);

            // Adiciona is_dirty para categorias
            data.categorias = data.categorias.map(categoria => {
                const original = existingCards.find(card => card?.produtos[0]?.idCategoria === categoria.id_categoria);

                const isCategoriaDirty = !original || (
                    categoria.quantidade_maxima !== original.quantidade_maxima ||
                    categoria.periodo !== original.periodo ||
                    categoria.ponto_alerta !== original.ponto_alerta ||
                    categoria.deletePermissions === true
                );

                return {
                    ...categoria,
                    is_dirty: isCategoriaDirty
                };
            });

            for (let categoria of data.categorias.filter(c => !c.deletePermissions)) {
                const permissoesAtivas = data.permissoes.filter(p => p.id_categoria === categoria.id_categoria && p.status);

                if (permissoesAtivas.length === 0) {
                    exibirAlerta(
                        'Ops',
                        `Há uma categoria que se encontra com todas as permissões inativas, é necessário ter pelo menos uma permissão ativa. Para remover todas, exclua a categoria.`,
                        'error'
                    );
                    return;
                }
            }
        }

        if (data.permissoes) {
            data.permissoes = data.permissoes.filter(permissao => permissao != null);
            // Adiciona is_dirty para permissões
            data.permissoes = data.permissoes.map(permissao => {
                const categoria = data.categorias.find(cat => cat.id === permissao.id_categoria);
                if (categoria) permissao.quantidade_maxima = categoria.quantidade_maxima;

                const permissaoOriginal = existingCards.find(card =>
                    card.produtos.some(prod => prod.id === permissao.id_produto)
                );

                if (!permissaoOriginal) {
                    return {
                        ...permissao,
                        is_dirty: true
                    };
                }

                const isPermissaoDirty = (
                    permissao.periodoAntigo !== permissao.periodo ||
                    permissao.ponto_alerta !== permissao.ponto_alertaAntigo ||
                    permissao.statusAntigo !== permissao.status ||
                    permissao.sobrescreverCategoriaAntigo !== permissao.sobrescreverCategoria
                );

                return {
                    ...permissao,
                    is_dirty: isPermissaoDirty
                };
            });
        }


        if (!isEditAction) {
            data.status = true;
        }

        if (enviandoRef.current) return;
        enviandoRef.current = true;

        try {
            exibirDialog('Realizando operação. Aguarde...');

            const response = await manipulaGHEs(data, isEditAction);

            acompanhar({
                id: response?.data?.id_processamento,
                id_grupo: selectedGHE?.id ?? response?.data?.id,
                nome_grupo: data.nome ?? selectedGHE?.nome,
                id_empresa: data.id_empresa ?? selectedGHE?.id_empresa,
                acao: isEditAction ? 'atualizar' : 'criar',
            });

            setOpen(false);
            exibirAlerta('Sucesso', `Grupo ${isEditAction ? 'editado' : 'cadastrado'} com sucesso.`, 'success');
        } catch (error) {
            console.log('Erro ao salvar GHE:', error);

            if (isGrupoTravado(error)) {
                queryClient.invalidateQueries({ queryKey: ['processamentosAtivos'] });
                exibirAlerta('Grupo em processamento', mensagemGrupoTravado(error), 'warning');
                return;
            }

            exibirAlerta('Erro', `Erro ao ${isEditAction ? 'editar' : 'cadastrar'} o Grupo.`, 'error');
        } finally {
            enviandoRef.current = false;
            queryClient.invalidateQueries({ queryKey });
            handleCloseDialog();
        }
    };

    useEffect(() => {
        if (open) {
            enviandoRef.current = false;
            carregarEmpresas();

            // set default values to form
            const defaultValues = {
                id_grupo: selectedGHE?.id ?? '',
                nome: selectedGHE?.nome ?? '',
                status: selectedGHE?.status ?? true,
                descricao: selectedGHE?.descricao ?? '',
                observacao: selectedGHE?.observacao ?? '',
                id_empresa: selectedGHE?.id_empresa ?? '',
                cod_externo: selectedGHE?.cod_externo ?? '',
            };

            reset(defaultValues);

            if (isEditAction) {
                setCards([]);
                loadCompanyData(selectedGHE.id);
            }

            setSelectedTab(0)
        } else {
            // limpa o formulário ao sair
            reset();
            setCards([]);
            setSelectedCard(null);
            setCategoriasEProdutos([]);
            setPermissoes([]);
            setSelectedCardInfo([]);
            setAlreadyLoadInitialCards(false);
            setGheEmpresaId(null);
        }
    }, [open]);

    useEffect(() => {
        if (isEditAction && categoriasEProdutos.length > 0 && permissoes.length > 0) {
            createCardsInEdit();
        }
    }, [categoriasEProdutos, permissoes]);

    useEffect(() => {
        if (open && isEditAction) {
            if (!alreadyLoadInitialCards) {
                exibirDialog('Carregando permissões...');
            } else {
                handleCloseDialog();
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alreadyLoadInitialCards, isEditAction, open]);

    useEffect(() => {
        if (selectedGHE && !openDeleteDialog) {
            setGheEmpresaId(selectedGHE.id_empresa);
            carregarPessoas();
        }
    }, [selectedGHE]);

    useEffect(() => {
        if (categoriasEProdutos && categoriasEProdutos.length > 0) {
            const extractedProducts = categoriasEProdutos.flatMap(categoria =>
                categoria.produtos?.map(produto => ({
                    ...produto,
                    idCategoria: categoria.id,
                    nomeCategoria: categoria.nome,
                    periodo_categoria: categoria.periodo,
                    ponto_alerta_categoria: categoria.ponto_alerta,
                    quantidade_maxima_categoria: categoria.quantidade_maxima,
                    disabled: produto.inativo === true,
                    label: produto.nome + (produto.inativo ? ' - (Inativo)' : ''),
                })) || []
            );

            setProdutos(extractedProducts);
        }
    }, [categoriasEProdutos]);

    return (
        <Dialog open={open} fullWidth maxWidth={'xl'}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <>
                    {openCategoryDetailDialog
                        ? <DialogTitle>Visualizando GHE: <strong>{selectedGHE.nome}</strong></DialogTitle>
                        : isEditAction
                            ? <DialogTitle>Editando GHE: <strong>{selectedGHE.nome}</strong></DialogTitle>
                            : <DialogTitle>Cadastrar novo GHE</DialogTitle>
                    }

                    <DialogContent sx={{ minHeight: '65vh' }}>
                        <DialogContentText sx={{ mb: 2 }}>
                            {openCategoryDetailDialog
                                ? 'Visualize as categorias e permissões defininidas para esse GHE'
                                : isEditAction
                                    ? 'Preencha corretamente os dados.'
                                    : 'Preencha corretamente os dados abaixo para cadastrar um novo Grupo Homogêneo de Exposição.'}
                        </DialogContentText>

                        {openCategoryDetailDialog ? (
                            <Tabs
                                value={selectedTab}
                                onChange={(event, newValue) => {
                                    setSelectedTab(newValue);
                                }}
                                textColor="primary"
                                variant="fullWidth"
                                sx={{ height: { md: '48px' }, minHeight: { md: '48px' } }}
                                aria-label="tabs formulário de grupos"
                            >
                                <Tab
                                    label="Categorias e Permissões"
                                    icon={<Category fontSize='small'/>}
                                    iconPosition="start"
                                    sx={{ minHeight: { md: '48px' } }}
                                />
                            </Tabs>
                        ) : (
                            <Tabs
                                value={selectedTab}
                                onChange={(event, newValue) => {
                                    setSelectedTab(newValue);
                                }}
                                textColor="primary"
                                variant="fullWidth"
                                sx={{ height: { md: '48px' }, minHeight: { md: '48px' } }}
                                aria-label="tabs formulário de grupos"
                            >
                                <Tab
                                    label="Instruções"
                                    icon={<InspectionIcon fontSize='small'/>}
                                    iconPosition="start"
                                    sx={{ minHeight: { md: '48px' } }}
                                />

                                <Tab
                                    label="Cadastro"
                                    icon={<AppRegistration fontSize='small'/>}
                                    iconPosition="start"
                                    sx={{ minHeight: { md: '48px' } }}
                                />

                                {isEditAction & !desativado &&
                                    <Tab
                                        label="Colaboradores"
                                        icon={<Person fontSize='small'/>}
                                        iconPosition="start"
                                        sx={{ minHeight: { md: '48px' } }}
                                    />
                                }
                            </Tabs>
                        )}

                        {!openCategoryDetailDialog ? (
                            <>
                                {/* ABA DE INSTRUÇÕES */}
                                <Box hidden={(selectedTab !== 0 && !openCategoryDetailDialog)} sx={{ p: 2, backgroundColor: "#fafafa", borderRadius: 2 }}>
                                    <GHEFormFirstStep />
                                </Box>

                                {/* ABA DE CADASTRO */}
                                <Box hidden={(selectedTab !== 1)} sx={{ p: 2, backgroundColor: '#fafafa' }}>
                                    <GHEFormSecondStep
                                        empresas={empresas}
                                        carregaCategorias={carregaCategorias}
                                        produtos={produtos}
                                        allowInteraction={allowInteraction}
                                        handleCloseDialog={handleCloseDialog}
                                        isEditAction={isEditAction}
                                        isLoading={isLoading}
                                        getFormErrorMessage={getFormErrorMessage}
                                        setValue={setValue}
                                        getValues={getValues}
                                        register={register}
                                        unregister={unregister}
                                        errors={errors}
                                        watch={watch}
                                        control={control}
                                        setCards={setCards}
                                        cards={cards}
                                        existingCards={existingCards}
                                        setSelectedCard={setSelectedCard}
                                        selectedCard={selectedCard}
                                        setSelectedCardInfo={setSelectedCardInfo}
                                        selectedCardInfo={selectedCardInfo}
                                        categoriasEProdutos={categoriasEProdutos}
                                        selectedGHE={selectedGHE}
                                        setGheEmpresaId={setGheEmpresaId}
                                        gheEmpresaId={gheEmpresaId}
                                        exibirAlerta={exibirAlerta}
                                    />
                                </Box>

                                <Box hidden={(!isEditAction || selectedTab !== 2)} sx={{ p: 2, backgroundColor: "#fafafa", borderRadius: 2 }}>
                                    <GHEFormThirdStep
                                        pessoas={pessoas}
                                    />
                                </Box>
                            </>
                        ) : (
                            <>
                                {/* ABA DE CATEGORIAS E PERMISSÕES */}
                                <Box hidden={(selectedTab !== 0 && openCategoryDetailDialog)} sx={{ p: 2, backgroundColor: "#fafafa", borderRadius: 2 }}>
                                    <GHECategoryDetails data={selectedGHE} />
                                </Box>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        {openCategoryDetailDialog ? (
                            <Button
                                onClick={() => {
                                    setOpen(false);
                                    setOpenCategoryDetailDialog(false);
                                }}
                                variant="contained"
                            >
                                Fechar
                            </Button>
                        ) : allowInteraction ? (
                            <>
                                <Button
                                    onClick={() => {
                                        setOpen(false);
                                        setOpenCategoryDetailDialog(false);
                                    }}
                                    variant="outlined"
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </Button>

                                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                                <Button
                                    variant="contained"
                                    type="submit"
                                    disabled={isSubmitting}
                                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                                >
                                    {isSubmitting ? 'Salvando…' : 'Salvar'}
                                </Button>
                            </>
                        ) : (
                            <Button onClick={() => setOpen(false)} variant="contained">
                                Fechar
                            </Button>
                        )}
                    </DialogActions>
                </>
            </form>
        </Dialog>
    );
}

export default GHEForm;